import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useForm } from "react-hook-form";
import axios from "axios";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";

const baseUrl = process.env.REACT_APP_BASE_URL;
const FeuC11FuelConsumption = forwardRef(({ c11FuelConsumption, dropdownData, refetchMonitoringData, shouldRefetch,vesselMstId }, ref) => {
  const RowActions = {
    Edit: 0,
    Add: 1,
  };
  const methods = useForm({});
  const { reset } = methods;
  const [activeAction, setactiveAction] = useState(-1);
  const [validationErrors, setValidationErrors] = useState({});
  const [fuelData, setFuelData] = useState([]);

  const [hasFetched, setHasFetched] = useState(false);
  const fuelTypeOptions = useSelector(
    (state) => state.myFleet.mrvDropdowns?.allfueltypes || []
  );

  useEffect(() => {
    if (shouldRefetch && !hasFetched) {
      fetchFuelConsumers();
      setHasFetched(true);
    }
  }, [shouldRefetch]);


  useEffect(() => {
    if (!shouldRefetch) {
      setHasFetched(false);
    }
  }, [shouldRefetch]);


  useEffect(() => {
    if (Array.isArray(c11FuelConsumption)) {
      const transformedData = c11FuelConsumption?.map((item) => {
        return {
          emissionSourceRefNo: item?.emissionSourceRefNo || '',
          emissionSourceName: item?.emissionSourceName || '',
          emissionSourceType: item?.emissionSourceType || '',
          emissionGuid:item?.emissionGuid || "",
          equipmentName: item?.equipmentName || "",
          chosenMethods: Array.isArray(item.chosenMethods)
            ? item.chosenMethods
            : typeof item.chosenMethods === 'string'
              ? item.chosenMethods.split(',')?.map((v) => v.trim())
              : [],
        };
      });

      setFuelData(transformedData);
    }
  }, [c11FuelConsumption]);


  const getFuelConsumers = () => {
    return (
      dropdownData?.monitoringMethodOptions.map((type) => ({
        label: type.name,
        value: type.code,
      })) || []
    );
  };
  const emissionSourceTypeOptions = useMemo(getFuelConsumers, [dropdownData]);
  const typeLabelByCode = useMemo(
    () => Object.fromEntries(emissionSourceTypeOptions?.map(o => [o.value, o.label])),
    [emissionSourceTypeOptions],
  );
  useEffect(() => {
    if (c11FuelConsumption) {
      reset({
        emissionSourceRefNo: c11FuelConsumption.emissionSourceRefNo || "",
        emissionSourceName: c11FuelConsumption.emissionSourceName || "",
        emissionSourceType: c11FuelConsumption.emissionSourceType || "",
        equipmentName: c11FuelConsumption.equipmentName || "",
        chosenMethods: c11FuelConsumption.chosenMethods || []
      });
    }
  }, [c11FuelConsumption, reset]);

  const handleFuelActions = async (table, row, action) => {
    if (activeAction === -1) {
      if (action === RowActions.Edit) {
        table.setEditingRow(row);
        setactiveAction(action);
      } else if (action === RowActions.Add) {
        table.setCreatingRow(true);
        setactiveAction(action);
      }
      return;
    } else return;
  };

  const resetActiveRow = () => {
    setactiveAction(-1);
    setValidationErrors({});
  };

  const validateRequired = (value) => {
    if (typeof value === "string" || Array.isArray(value)) {
      return !!value?.length;
    }
    return false;
  };

  const validateField = (row) => {
    const errors = {};
    if (!validateRequired(row["chosenMethods"])) {
      errors.chosenMethods = "Field is required";
    }
    if (!validateRequired(row["equipmentName"])) {
      errors.equipmentName = "Field is required";
    }

    return errors;
  };

  useImperativeHandle(ref, () => ({
    async submitAndCheck() {
      const { creatingRow, editingRow } = table.getState();
      if (!creatingRow && !editingRow) return true;
      const mode = creatingRow ? "add" : "edit";
      const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
      let isDirty = false;
      if (editingRow && editedValues) {
        isDirty = Object.keys(editedValues).some(
          (key) => editedValues[key] !== editingRow.original[key]
        );
      }
      const validationRes = validateField(editedValues, mode, {});
      const hasErrors = Object.values(validationRes).some((err) => err);

      if (hasErrors) {
        setValidationErrors(validationRes);
        emitToasterMsg(
          "warning",
          "Validation Error",
          "Please correct the highlighted fields before proceeding."
        );
        return false;
      }

      // Autosave on valid & dirty
      if (editingRow && isDirty) {
        await handleUpdateFuelConsumption({ values: editedValues, table, row: editingRow }, true);
        return true;
      }
      if (!isDirty) {
        setactiveAction(-1)
        table.setEditingRow(null);
        return true;
      }
      return true;
    }
  }));

  const handleUpdateFuelConsumption = async ({ values, table, row }, isAuto = false) => {

    const newValidationErrors = validateField(values, "add", {});
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});

    const updatedData = fuelData.map((item, index) => {
      if (index === row.index) {
        return {
          ...item,
          emissionSourceRefNo: values?.emissionSourceRefNo,
          emissionSourceName: values?.emissionSourceName,
          emissionSourceType: values?.emissionSourceType,
          emissionGuid: item?.emissionGuid || "", 
          equipmentName: values?.equipmentName,
          chosenMethods: values?.chosenMethods || [],
        };
      }
      return item;
    });
    const finalPayload = updatedData?.map((item) => ({
      ...item,
      C21GreenhouseEmissionsId: 0
    }));

    try {
      const C11FuelConsumption = await callApi({
        method: "POST",
        url: API_ROUTES.C11(vesselMstId),
        data: finalPayload,
      });
      if (C11FuelConsumption?.isSuccess) {
        emitToasterMsg("success", "Table C.1.1", isAuto ? "Emission source methods has been autosaved successfully" : "Emission source methods has been updated successfully");
        // emitToasterMsg("success", "Table C21", "Emission source has been updated successfully");
        table.setEditingRow(null);
        setactiveAction(-1);
        setFuelData(finalPayload);
        if (typeof refetchMonitoringData === "function") {
          await refetchMonitoringData();
        }
      }
      else {
        emitToasterMsg("error", "Table C.1.1", C11FuelConsumption?.errorMessage);
      }
    } catch (error) {
      emitToasterMsg("error", "Table C.1.1", error.errorMessage);
    }
  };

  useEffect(() => {
    if (c11FuelConsumption) {
      reset(prev => ({ ...prev, c12: c11FuelConsumption }));
    }
  }, [c11FuelConsumption, reset]);

  const hasMonitoringName = fuelData.some(item => item.monitoringName);

  const fetchFuelConsumers = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}api/FuelEuMonitoringPlan/partc/GetC11DataByVesselId?vesselMstId=${vesselMstId}`
      );

      if (Array.isArray(response.data)) {
        const formattedData = response.data.map((item) => ({
          emissionSourceRefNo: item.emissionSourceRefNo,
          emissionSourceName: item.emissionSourceName,
          emissionSourceType: item.emissionSourceType,
          equipmentName: item.equipmentName,
          chosenMethods: item.chosenMethods || [],
        }));

        setFuelData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching emission sources:", error);
    }
  };

  const getEmissionSourceTypeLabel = (code) => {
    const foundOption = dropdownData.emissionSourceTypes?.find(
      (option) => option.code === code
    );
    return foundOption ? foundOption.name : code;
  };

  useEffect(() => {
    if (!Array.isArray(c11FuelConsumption) || c11FuelConsumption.length === 0) {
      fetchFuelConsumers();
    }
  }, [vesselMstId, c11FuelConsumption]);

  useEffect(() => {
    if (c11FuelConsumption) {
      reset(prev => ({ ...prev, c11: c11FuelConsumption }));
    }
  }, [c11FuelConsumption, reset]);
  const columns = useMemo(
    () => [
      {
        accessorKey: "emissionSourceRefNo",
        header: "N\u2070 fuel consumer unit",
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },

      },
      {
        accessorKey: "emissionSourceName",
        header: "Name of the fuel consumer unit",
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
      },

      {
        accessorKey: "emissionSourceType",
        header: "fuel consumer type",
        enableEditing: false,
        Cell: ({ cell }) => {
          const code = cell.getValue();
          return getEmissionSourceTypeLabel(code);
        },
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
      },
      {
        accessorKey: "chosenMethods",
        header: "Monitoring method",
        editVariant: 'select',
        size: 280,
        editSelectOptions: dropdownData?.monitoringMethodOptions?.map(option => ({
          label: option.name,
          value: option.code
        })) || [],
        muiEditTextFieldProps: ({ cell, row }) => ({
          select: true,
          required: true,
          value: cell.getValue() || [],
          error: !!validationErrors?.chosenMethods,
          helperText: validationErrors?.chosenMethods,
          SelectProps: {
            multiple: true,
            renderValue: (selected) => {
              if (!selected || selected.length === 0) return 'Select monitoring method(s)';
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((code) => {
                    const option = dropdownData?.monitoringMethodOptions?.find(opt => opt.code === code);
                    return (
                      <Chip
                        key={code}
                        label={option?.name || code}
                        onDelete={(e) => {
                          e.stopPropagation();
                          const newValue = selected.filter((v) => v !== code);
                          row._valuesCache["chosenMethods"] = newValue;
                          setValidationErrors((prev) => ({
                            ...prev,
                            chosenMethods: undefined,
                          }));
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    );
                  })}
                </Box>
              );
            }
          },
          onChange: (event) => {
            const value = event.target.value;
            row._valuesCache["chosenMethods"] = value;
            setValidationErrors((prev) => ({
              ...prev,
              chosenMethods: undefined,
            }));
          },
        }),
        Cell: ({ cell }) => {
          const selectedCodes = cell.getValue() || [];
          return selectedCodes.map(code => {
            const option = dropdownData?.monitoringMethodOptions?.find(opt => opt.code === code);
            return option?.name || code;
          }).join(', ');
        },
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle",
          },
        },
      },
      {
        accessorKey: "equipmentName",
        header: "Measuring equipment(name)",
        enableEditing: true,
        muiEditTextFieldProps: () => ({
          required: true,
          error: !!validationErrors?.equipmentName,
          helperText: validationErrors?.equipmentName,
          onChange: () => {
            setValidationErrors((prev) => ({
              ...prev,
              equipmentName: undefined,
            }));
          },
        }),
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
      },

    ]

  )

  const table = useMaterialReactTable({
    columns,
    data: fuelData || [],
    enableStickyHeader: true,
    enableSorting: false,
    enableRowSelection: false,
    enableColumnPinning: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    enablePagination: false,
    muiTableHeadCellProps: {
      sx: {
        color: "rgb(255 255 255)",
        backgroundColor: "#042a4c",
        position: "sticky",
        borderRight: "1px solid lightgray",
        "&:last-child": {
          borderRight: "none",
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "520px",
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
          },
        },
      },
    },
    muiTableBodyCellProps: ({ row, cell }) => {
      return {
        sx: {
          borderRight: "1px solid lightgray",
          "&:last-child": {
            borderRight: "none",
          },
          color: "",
        },
      };
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        "&:not(:last-of-type)": {
          borderBottom: "1px solid lightgray",
        },
        verticalAlign: "top",
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        borderBottom: "2px solid lightgray",
      },
    },
    columnOrder: [
      "emissionSourceRefNo",
      "emissionSourceName",
      "emissionSourceType",
      "chosenMethods",
      "equipmentName"

    ],

    getRowId: (row) => { },

    onEditingRowCancel: resetActiveRow,
    onCreatingRowCancel: resetActiveRow,
    onEditingRowSave: handleUpdateFuelConsumption,
    renderToolbarInternalActions: ({ table }) => <></>,
    renderRowActions: ({ row, table }) => {
      return (
        <Box sx={{ display: "flex", gap: "1rem" }}>
          <Tooltip title="Edit">
            <IconButton
              style={{ padding: "0px" }}
              onClick={() =>
                handleFuelActions(
                  table,
                  row,
                  RowActions.Edit
                )
              }
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

        </Box>
      );
    },

  });

  return (
    <div className="mt-1">
      <MaterialReactTable table={table} />
    </div>
  );
})

export default FeuC11FuelConsumption;